"use client"

import { useEffect, useRef, useState } from 'react'
import SvgIcon from '@components/shared/svg-icons/SvgIcon'
import mix from '@utils/styles/mix'
import Turnstile from 'src/pagesComponents/display/v2/Turnstile'
import { chatApi, errorOf } from './api'
import { DialogHead, useDialog } from './Dialogs'
import Thumb from './Thumb'
import { SAFETY } from './ConversationPane'

import styles from './Messages.module.css'

const MAX = 2000

// Telefon: okno w widocznej części ekranu nad klawiaturą (visualViewport) — wysokość i odstęp od dołu jako zmienne CSS.
function useKeyboardFit (ref) {
    useEffect(() => {
        const vv = window.visualViewport
        const d = ref.current
        if (!vv || !d) return
        const fit = () => {
            const kb = Math.max(0, window.innerHeight - vv.offsetTop - vv.height)
            d.style.setProperty('--vv-h', `${Math.round(vv.height)}px`)
            d.style.setProperty('--vv-kb', `${Math.round(kb)}px`)
            const el = document.activeElement
            if (kb > 0 && el && d.contains(el)) el.scrollIntoView({ block: 'nearest' })
        }
        fit()
        vv.addEventListener('resize', fit)
        vv.addEventListener('scroll', fit)
        return () => { vv.removeEventListener('resize', fit); vv.removeEventListener('scroll', fit) }
    }, [ref])
}

// Gotowe pytanie zakończone „…?” to początek pytania do uzupełnienia (wstawiany bez „…?”, z kursorem na końcu).
const PROMPT = '…?'
const presetText = (p) => (p.endsWith(PROMPT) ? `${p.slice(0, -PROMPT.length)} ` : p)

// Okno pierwszej wiadomości z ogłoszenia albo z wizytówki firmy (firm): gotowe wiadomości (kliknięcie wstawia tekst do
// edycji), pole, ostrzeżenie, błędy z serwera (link, słowo, limit, Turnstile); gość — „Zaloguj się i wyślij” (tekst
// zostaje zapisany).
export default function WriteDialog ({ ad, card, draft = '', firm, guest, onClose, onLogin, onSent, opener, presets = [], token }) {
    const taRef = useRef(null)
    const dialog = useDialog(onClose, opener, taRef)
    useKeyboardFit(dialog.ref)
    const [text, setText] = useState(draft)
    const [problem, setProblem] = useState(null)
    const [challenge, setChallenge] = useState(null)
    const [busy, setBusy] = useState(false)
    const alertRef = useRef(null)
    const lines = text.split('\n').map(l => l.trim())
    const pressed = (p) => (p.endsWith(PROMPT) ? lines.some(l => l.startsWith(presetText(p).trim())) : lines.includes(p))

    useEffect(() => { alertRef.current?.scrollIntoView({ block: 'nearest' }) }, [problem])

    const pick = (preset) => {
        const p = presetText(preset)
        const value = text.trim()
        const onlyPresets = !value || value.split('\n').every(l => presets.includes(l.trim()))
        const next = !value ? p : onlyPresets && value.split('\n').includes(p) ? value : `${value}\n${p}`
        setText(next)
        requestAnimationFrame(() => { const ta = taRef.current; if (ta) { ta.focus(); ta.setSelectionRange(next.length, next.length) } })
    }

    const submit = async (e, turnstile) => {
        e?.preventDefault?.()
        const value = text.trim()
        if (!value) { setProblem({ title: 'Wpisz wiadomość', text: 'Wybierz gotowe pytanie albo napisz własne.' }); taRef.current?.focus(); return }
        if (value.length > MAX) { setProblem({ title: 'Wiadomość jest za długa', text: `Skróć ją do ${MAX} znaków (teraz: ${value.length}).` }); return }
        if (guest) return onLogin(text)
        setBusy(true)
        const r = firm
            ? await chatApi(token, '/api/messages/start-salon', { method: 'POST', body: { salonId: firm._id, text: value, turnstile } })
            : await chatApi(token, '/api/messages/start', { method: 'POST', body: { type: ad.type, shortid: ad.shortid, buy: Boolean(ad.buy), text: value, turnstile } })
        setBusy(false)
        if (r.body.success) return onSent(r.body.id)
        if (r.body.challenge && r.body.siteKey) {
            setChallenge({ siteKey: r.body.siteKey, key: Date.now() })
            setProblem({ title: 'Potwierdź, że jesteś człowiekiem', text: errorOf(r), warn: true })
            return
        }
        setChallenge(null)
        const title = { link: 'Usuń link', word: 'Zmień słowo', blocked: 'Nie możesz wysłać wiadomości', same: 'Ta sama wiadomość do wielu osób', limit: 'Limit nowych rozmów', rate: 'Za szybko' }[r.body.code] || 'Nie wysłano'
        setProblem({ title, text: errorOf(r) })
        taRef.current?.focus()
    }

    return (
        <dialog aria-labelledby="write-h" className={mix([styles.dialog, styles.wDialog])} {...dialog}>
            <DialogHead id="write-h" onClose={onClose} title={firm ? 'Wiadomość do firmy' : ad?.buy ? 'Wiadomość do kupującego' : 'Wiadomość do sprzedającego'} />
            <form className={styles.wForm} noValidate onSubmit={submit}>
                <div className={mix([styles.dBody, styles.wScroll])}>
                    { firm
                        ? (
                            <div className={styles.mOffer}>
                                <Thumb salon={{ logo: firm.logo }} />
                                <span><b>{firm.name}</b><small>{[firm.city, 'zapytanie z wizytówki'].filter(Boolean).join(' · ')}</small></span>
                            </div>
                        )
                        : (
                            <div className={styles.mOffer}>
                                <Thumb offer={{ photo: card?.photo, state: 'active' }} />
                                <span><b>{card?.title}</b>{ card?.price && <small>{card.price}</small> }</span>
                            </div>
                        ) }
                    { presets.length > 0 && (
                        <fieldset className={styles.fieldset}>
                            <legend className={styles.legend}>{ad?.buy ? 'Gotowe wiadomości' : 'Gotowe pytania'} <span className={styles.req}>— kliknij, potem możesz zmienić tekst</span></legend>
                            <div className={styles.presets}>
                                { presets.map(p => <button aria-pressed={pressed(p)} className={styles.preset} key={p} onClick={() => pick(p)} type="button">{p}</button>) }
                            </div>
                        </fieldset>
                    ) }
                    <div className={styles.field}>
                        <label htmlFor="write-text">Twoja wiadomość</label>
                        <textarea aria-describedby="write-count" className={styles.control} id="write-text" maxLength={MAX + 500} onChange={(e) => setText(e.target.value)} ref={taRef} rows={4} value={text} />
                        <span aria-live="polite" className={styles.small} id="write-count">{text.length > MAX - 200 ? `${text.length} / ${MAX}` : ''}</span>
                    </div>
                    <p className={styles.mSafety}><SvgIcon name="shield" size="sm" /><span>{SAFETY}</span></p>
                    { problem && <div className={styles.alert} ref={alertRef} role="alert"><strong>{problem.title}</strong><p>{problem.text}</p></div> }
                    { challenge && <div className={styles.wChallenge}><Turnstile key={challenge.key} onRender={(el) => el.scrollIntoView({ block: 'nearest' })} onToken={(t) => { setProblem(null); submit(null, t) }} siteKey={challenge.siteKey} /></div> }
                    { guest && <p className={styles.small}>Aby wysłać wiadomość, zaloguj się. Tekst zachowamy — po zalogowaniu wrócisz do {firm ? 'tej wizytówki' : 'tego ogłoszenia'} z gotową wiadomością.</p> }
                </div>
                <div className={mix([styles.dActs, styles.wActs])}>
                    <button className={mix([styles.btn, styles.secondary])} onClick={onClose} type="button">Anuluj</button>
                    <button className={mix([styles.btn, styles.primary])} disabled={busy} type="submit">{guest ? 'Zaloguj się i wyślij' : 'Wyślij'}</button>
                </div>
            </form>
        </dialog>
    )
}
